export const QUEUES = {
  maintenance: { concurrency: 1 },
  totals: { concurrency: 1 },
  rates: { concurrency: 1 },
  imports: { concurrency: 2 },
  files: { concurrency: 2 },
  /**
   * Recurring giving's own queue, at concurrency 1 per process
   * (docs/decisions/0043-the-worker.md §6.4's catalogue rows). Charging a
   * donor's card is the one job in the system that costs somebody money if it
   * runs twice, and on `maintenance` a night of charges would sit behind the
   * outbox sweep, the watchdog and every retention pass — and hold them back in
   * turn. Concurrency 1 limits one process and nothing across replicas: what
   * keeps two replicas from charging one period is the `pledge_charges` unique
   * index and the pledge's row lock, not a Redis lock.
   */
  payments: { concurrency: 1 },
  /**
   * One queue per notification channel, each with its own BullMQ rate limiter —
   * docs/plans/07-notifications-and-reporting.md §7.1 and
   * docs/decisions/0043-the-worker.md. On one queue a throttled SMS provider
   * would hold every receipt e-mail behind it, and the two have nothing to do
   * with each other. The limiter is kept in Redis per queue, so it holds across
   * replicas rather than per process; the numbers are deliberately below what
   * each provider publishes, because being slow costs nothing and being blocked
   * by a provider costs a day.
   */
  'notify-email': { concurrency: 4, limiter: { max: 20, duration: 1_000 } },
  'notify-sms': { concurrency: 2, limiter: { max: 5, duration: 1_000 } },
  'notify-whatsapp': { concurrency: 2, limiter: { max: 10, duration: 1_000 } },
  'notify-push': { concurrency: 2, limiter: { max: 20, duration: 1_000 } },
} as const satisfies Record<
  string,
  { concurrency: number; limiter?: { max: number; duration: number } }
>;

export type QueueName = keyof typeof QUEUES;

/** How fast a queue's worker may start jobs, where its provider sets a ceiling. */
export type QueueLimiter = { max: number; duration: number };

export function queueLimiter(queue: QueueName): QueueLimiter | undefined {
  const declared = QUEUES[queue] as { limiter?: QueueLimiter };
  return declared.limiter;
}

export const QUEUE_NAMES = Object.keys(QUEUES) as QueueName[];
